import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { useGetUserQuery } from 'common'
import { currentTierSelector, isOnFreeStageSelector } from 'features/billing'
import { useWasInNotetakerIntroExperiment } from 'hooks/growth_experiments/use-was-in-notetaker-intro-experiment'
import { MEETINGS_TAB_BASE_PATH_REBRAND } from 'hooks/home/meetings_tab_paths'
import { useNotetakerGrowthUiVisible } from 'hooks/notetaker_visibility'
import { useNotetakerFiveFreeRecapsExperiment } from 'hooks/useNotetakerFiveFreeRecapsExperiment'

import { useGetNotetakerFreeMeetingsQuotaQuery } from '../api'
import { WIZARD_MODAL_SEEN_MAX, SESSION_KEY, MR_ONBOARDING_WIZARD } from '../constants'
import { showAcknowledgementModal } from '../modals/wizard_acknowledgement_modal/acknowledgement_modal_slice'
import { getAcknowledgementModalIsUserTriggered } from '../selectors'


export const useShouldDisplayOnboardingWizard = (MiUserDataLoading, MiUserData, update) => {
  const dispatch = useDispatch()
  const ref = useRef()
  const { pathname } = useLocation()
  const isCurrentSession = sessionStorage.getItem(SESSION_KEY) === 'true'
  const isUserTriggered = useSelector(getAcknowledgementModalIsUserTriggered)
  const {
    isVisible: growthUiVisible,
    isLoading: isGrowthUiVisibilityLoading
  } = useNotetakerGrowthUiVisible()
  const wasInNotetakerIntroExperiment = useWasInNotetakerIntroExperiment()
  const isFreeUser = useSelector(isOnFreeStageSelector)
  const currentTier = useSelector(currentTierSelector)
  const { isInExperiment: isInFiveFreeRecapsExperiment } = useNotetakerFiveFreeRecapsExperiment(isFreeUser)
  const { data: userData } = useGetUserQuery()
  const userUuid = userData?.uuid
  const shouldSkipNotetakerQuotaQuery = !isFreeUser || !isInFiveFreeRecapsExperiment || !userUuid
  const {
    data: notetakerQuotaData,
    isLoading: isNotetakerQuotaLoading
  } = useGetNotetakerFreeMeetingsQuotaQuery(
    { userUuid },
    { skip: shouldSkipNotetakerQuotaQuery }
  )
  const hasRemainingFreeMeetings = notetakerQuotaData?.resource?.featureAvailable
  const isEligibleTier = ['standard_notetaker', 'teams_notetaker'].includes(currentTier)
  const isCalendarRoute = pathname === MEETINGS_TAB_BASE_PATH_REBRAND || pathname.startsWith(`${MEETINGS_TAB_BASE_PATH_REBRAND}/`)

  useEffect(() => {
    if (!ref.current && !MiUserDataLoading) {
      const attemptsRemaining = MiUserData?.onboardingWizardModalSeenCount

      if (isGrowthUiVisibilityLoading || !growthUiVisible) {
        return
      }

      if (!shouldSkipNotetakerQuotaQuery && isNotetakerQuotaLoading) {
        return
      }

      if (!isEligibleTier || !isCalendarRoute) {
        return
      }

      if (isUserTriggered || isCurrentSession) {
        ref.current = true
        return
      }

      if (wasInNotetakerIntroExperiment) {
        ref.current = true
        return
      }

      // Prevent modal from showing if admin has disabled notetaker for the organization
      if (MiUserData?.adminDisabledNotetaker) {
        ref.current = true
        return
      }

      if (attemptsRemaining === 0 || MiUserData?.miEnabled) {
        ref.current = true
        return
      }
      // Do not set ref.current when free so that it can be re-evaluated
      // after a potential upgrade by the user
      const isEligibleForFreeTierAutoOpen = isInFiveFreeRecapsExperiment && hasRemainingFreeMeetings
      if (isFreeUser && !isEligibleForFreeTierAutoOpen) {
        return
      }

      // Users first time going through the flow - true
      if (!Number.isInteger(attemptsRemaining)) {
        update({ onboardingWizardModalSeenCount: WIZARD_MODAL_SEEN_MAX })
        ref.current = true
        sessionStorage.setItem(SESSION_KEY, 'true')
        dispatch(showAcknowledgementModal(MR_ONBOARDING_WIZARD.AUTO_LOAD))
        return
      }

      // show modal and decrement
      if (attemptsRemaining > 0) {
        update({ onboardingWizardModalSeenCount: attemptsRemaining - 1 })
        sessionStorage.setItem(SESSION_KEY, 'true')
        ref.current = true
        dispatch(showAcknowledgementModal(MR_ONBOARDING_WIZARD.AUTO_LOAD))
      }
    }
  }, [
    MiUserData,
    MiUserDataLoading,
    ref,
    isCurrentSession,
    update,
    dispatch,
    isUserTriggered,
    growthUiVisible,
    isGrowthUiVisibilityLoading,
    wasInNotetakerIntroExperiment,
    isFreeUser,
    isInFiveFreeRecapsExperiment,
    hasRemainingFreeMeetings,
    shouldSkipNotetakerQuotaQuery,
    isNotetakerQuotaLoading,
    isEligibleTier,
    isCalendarRoute
  ])
}
